import {
  AccountingImportRowExportConfig,
  AccountingImportStatisticsExportConfig,
  ExportLicencesService,
  LicenceExportConfig,
  OptimizationExportConfig,
} from '@/client'
import { OpenAPI } from '@/client/core/OpenAPI'

function buildQueryString(queryParams: Record<string, unknown>) {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(queryParams)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      value.forEach(function appendArrayItem(item) {
        if (item === undefined || item === null) return
        searchParams.append(key, String(item))
      })
      continue
    }
    searchParams.append(key, String(value))
  }

  const serialized = searchParams.toString()
  return serialized ? `?${serialized}` : ''
}

function normalizeQueryKeys(queryParams: Record<string, unknown>) {
  const normalized: Record<string, unknown> = {}

  const queryOperatorSuffixMap: Array<[string, string]> = [
    ['Exists', '$exists'],
    ['Gte', '$gte'],
    ['Gt', '$gt'],
    ['Lte', '$lte'],
    ['Lt', '$lt'],
    ['Nin', '$nin'],
    ['Ne', '$ne'],
    ['All', '$all'],
    ['Size', '$size'],
    ['Eq', '$eq'],
    ['In', '$in'],
  ]

  const keyMap: Record<string, string> = {
    softwareNamesRegex: 'software_names.$regex',
    softwareNamesOptions: 'software_names.$options',
    aiPredictionEq: 'ai_prediction.$eq',
    predictionCertaintyGte: 'prediction_certainty.$gte',
    blacklistedEq: 'blacklisted.$eq',
    accountingImportId: 'accounting_import_id',
  }

  function toSnakeCase(value: string) {
    return value.replace(/([A-Z])/g, '_$1').toLowerCase()
  }

  function convertQueryKey(key: string) {
    const mappedKey = keyMap[key]
    if (mappedKey) {
      return mappedKey
    }

    for (const [suffix, operator] of queryOperatorSuffixMap) {
      if (!key.endsWith(suffix)) continue
      const base = key.slice(0, -suffix.length)
      if (!base) break
      return `${toSnakeCase(base)}.${operator}`
    }

    return toSnakeCase(key)
  }

  for (const [key, value] of Object.entries(queryParams)) {
    if (value === undefined || value === null) continue
    const mappedKey = convertQueryKey(key)
    normalized[mappedKey] = value
  }

  return normalized
}

// We call the endpoints manually with fetch so we get the raw Response,
// which is required to stream CSV/XLSX blobs. The generated client parses
// the body and would break binary downloads.

export async function exportResource({
  requestBody,
  queryParams,
}: {
  requestBody: LicenceExportConfig
  queryParams: Parameters<
    typeof ExportLicencesService.exportLicencesApiV1ExportLicencesPost
  >[0]
}) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', `Bearer ${OpenAPI.TOKEN}`)

  const body: BodyInit = JSON.stringify(requestBody)
  const url = `${OpenAPI.BASE}/api/v1/export/licences${buildQueryString(normalizeQueryKeys(queryParams))}`
  const requestOptions: RequestInit = {
    method: 'POST',
    headers: headers,
    body: body,
  }
  return await fetch(url, requestOptions)
}

export async function exportOptimizationsResource({
  requestBody,
  queryParams,
}: {
  requestBody: OptimizationExportConfig
  queryParams: Record<string, unknown>
}) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', `Bearer ${OpenAPI.TOKEN}`)

  const body: BodyInit = JSON.stringify(requestBody)
  const url = `${OpenAPI.BASE}/api/v1/export/optimizations${buildQueryString(normalizeQueryKeys(queryParams))}`
  const requestOptions: RequestInit = {
    method: 'POST',
    headers: headers,
    body: body,
  }
  return await fetch(url, requestOptions)
}

export async function exportAccountingImportStatistics({
  accountingImportId,
  requestBody,
  queryParams = { accountingImportId },
}: {
  accountingImportId: string
  requestBody: AccountingImportStatisticsExportConfig
  queryParams: Parameters<
    typeof ExportLicencesService.exportAccountingImportStatisticsApiV1ExportAccountingImportStatisticsAccountingImportIdPost
  >[0]
}) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', `Bearer ${OpenAPI.TOKEN}`)

  const body: BodyInit = JSON.stringify(requestBody)
  const url = `${OpenAPI.BASE}/api/v1/export/accounting-import-statistics/${encodeURIComponent(accountingImportId)}${buildQueryString(normalizeQueryKeys(queryParams))}`

  return await fetch(url, {
    method: 'POST',
    headers,
    body,
  })
}

export async function exportAccountingImportRows({
  requestBody,
  queryParams = {},
}: {
  requestBody: AccountingImportRowExportConfig
  queryParams: Parameters<
    typeof ExportLicencesService.exportAccountingImportRowsApiV1ExportAccountingImportRowsPost
  >[0]
}) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', `Bearer ${OpenAPI.TOKEN}`)

  const body: BodyInit = JSON.stringify(requestBody)
  const url = `${OpenAPI.BASE}/api/v1/export/accounting-import-rows${buildQueryString(normalizeQueryKeys(queryParams))}`

  return await fetch(url, {
    method: 'POST',
    headers,
    body,
  })
}
